import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchSanteMobilitePrice } from '../santeMobiliteThunk';
import { setPrice } from '../santeMobiliteSlice';
import { selectInclusionPath, selectIsInclusionStudent } from '../selectors';
import { computeMonthsCount } from '../utils';

interface SchoolWithPartnerLevel {
  partnerLevelHealthMobility?: number;
}

interface FetchPricePayload {
  stripe?: { unit_amount?: number | null };
}

const isZoneCovered = (z: string | number | undefined | null): boolean => {
  if (z === undefined || z === null) return false;
  const n = typeof z === 'string' ? parseInt(z, 10) : z;
  return n !== 0;
};

/**
 * Prix estimé, affiché juste sous la barre de progression.
 *
 * Remplace la barre sticky qui vivait en bas de l'écran. Elle avait le défaut
 * de demander à l'utilisateur de baisser les yeux hors du parcours pour
 * connaître son prix ; ici il est dans le champ de vision, à l'endroit où il
 * regarde déjà pour savoir où il en est. Les deux ont coexisté le temps d'un
 * déploiement, le même montant affiché deux fois — d'où ce remplacement.
 *
 * Ce composant est la SOURCE du prix, pas seulement son affichage : il porte
 * la logique de la barre supprimée, à l'identique.
 *
 *   - parcours inclusion gratuit → « Gratuit », aucun appel
 *   - parcours payant → appelle `/price` dès qu'on a dates + destination +
 *     réponse sécu, avec un debounce de 400 ms pour ne pas marteler le back
 *     quand l'utilisateur jongle entre la carte et la liste
 *   - zone 0 (pays non couvert) → rien : l'alerte de l'étape Destination dit
 *     déjà ce qu'il faut
 *
 * Rien au step 5 : le récapitulatif de finalisation affiche le prix en plus
 * détaillé, et le répéter au-dessus n'apporterait que du bruit.
 */
const PriceHeadline: React.FC = () => {
  const { t } = useTranslation('sante-mobilite');
  const dispatch = useAppDispatch();

  const step = useAppSelector(state => state.santeMobilite.step);
  const startDate = useAppSelector(state => state.santeMobilite.startDate);
  const endDate = useAppSelector(state => state.santeMobilite.endDate);
  const destination = useAppSelector(state => state.santeMobilite.destination);
  const hasFrenchSocialSecurity = useAppSelector(
    state => state.santeMobilite.hasFrenchSocialSecurity,
  );
  const price = useAppSelector(state => state.santeMobilite.price);
  const inclusionPath = useAppSelector(selectInclusionPath);
  const isInclusionStudent = useAppSelector(selectIsInclusionStudent);
  const school = useAppSelector(state => state.school?.school);

  const [loading, setLoading] = useState(false);
  const partnerLevel = useMemo(
    () => (school as SchoolWithPartnerLevel)?.partnerLevelHealthMobility ?? 0,
    [school],
  );

  const canEstimatePaid =
    !!startDate &&
    !!endDate &&
    !!destination &&
    typeof hasFrenchSocialSecurity === 'boolean' &&
    isZoneCovered(destination.zone);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (inclusionPath === 'free') {
      if (price !== 0) dispatch(setPrice(0));
      return;
    }

    if (!canEstimatePaid) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (cancelledRef.current) return;
      setLoading(true);
      const monthsCount = computeMonthsCount(
        startDate as string,
        endDate as string,
      );
      const res = await dispatch(
        fetchSanteMobilitePrice({
          zone: destination!.zone as string,
          monthsCount,
          partnerLevelHealthMobilityPrice: partnerLevel,
          hasFrenchSocialSecurity,
        }),
      );
      if (cancelledRef.current) return;
      if (fetchSanteMobilitePrice.fulfilled.match(res)) {
        const stripeAmount =
          (res.payload as FetchPricePayload)?.stripe?.unit_amount ?? null;
        if (stripeAmount != null) {
          dispatch(setPrice(stripeAmount / 100));
        }
      }
      setLoading(false);
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canEstimatePaid,
    inclusionPath,
    startDate,
    endDate,
    destination,
    hasFrenchSocialSecurity,
    partnerLevel,
  ]);

  const isFree = inclusionPath === 'free';

  // Étudiant inclusion qui n'a pas encore répondu à la question Sécu : on ne
  // sait PAS si son parcours sera gratuit. `computeInclusionPath` retombe sur
  // 'paid' par prudence, mais afficher ce tarif annoncerait un prix que son
  // école paie peut-être déjà — puis le ferait disparaître à l'étape 3. On
  // préfère ne rien affirmer tant qu'on ne sait pas.
  const inclusionUndecided =
    isInclusionStudent && hasFrenchSocialSecurity === null;

  if (step >= 5) return null;
  if (inclusionUndecided) return null;
  if (inclusionPath === 'paid' && !canEstimatePaid && price === null) {
    return null;
  }

  const priceLabel = isFree
    ? t('price_bar.free')
    : price !== null
    ? `${price.toFixed(2).replace('.', ',')} €`
    : '—';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        mt: 1.5,
      }}
      role="status"
      aria-live="polite"
    >
      <Typography
        variant="body2"
        sx={{ color: '#475a63', fontSize: { xs: '12px', sm: '14px' } }}
      >
        {t('price_bar.label')}
      </Typography>

      {loading && <CircularProgress size={16} sx={{ color: '#2f6975' }} />}

      <Typography
        component="span"
        sx={{
          color: isFree ? '#239f1b' : '#2f6975',
          fontWeight: 700,
          lineHeight: 1,
          fontSize: { xs: '22px', sm: '28px' },
        }}
      >
        {priceLabel}
      </Typography>

      <Tooltip title={t('price_bar.tooltip')} arrow>
        <InfoOutlinedIcon
          fontSize="small"
          sx={{ color: '#7a8a91', cursor: 'help' }}
        />
      </Tooltip>
    </Box>
  );
};

export default PriceHeadline;
